import { splitEncoding } from '@contentful/live-preview';
import type { SearchResponse } from 'instantsearch.js';

import type { PlaceholderProductListingOpts } from '@/components/Cms/PlaceholderProductListing';
import type {
  CollectionGroupWithAutomaticRefs,
  CollectionGroupWithManualRefs,
  CombinedCollectionGroupItem,
  ComponentChunk,
  EditorialSectionOpts,
} from '@/components/CollectionGroup/types';
import type { HeroProductListingOpts } from '@/components/HeroProductListing/types';
import type {
  ContentfulCollectionGroupFragment,
  ContentfulHeroProductListingFragment,
  ContentfulPlaceholderFragment,
} from '@/graphql/contentful/api';
import {
  applyProductConfigurationFilters,
  filterHits,
  getIndexName,
  ProductConfigurationFilterType,
} from '@/integrations/algolia/helpers';
import { createSearchClient } from '@/integrations/algolia/searchClientBuilder';
import type { WatchHit } from '@/integrations/algolia/types';
import type { ContentfulData } from '@/integrations/contentful';
import { getOptsForType } from '@/integrations/contentful';

export const buildEditorialComponentChunks = (
  editorialCmsData: ContentfulData[],
  sectionOpts: EditorialSectionOpts,
): ComponentChunk[] => {
  const chunks: ComponentChunk[] = [];
  let currentCollectionGroup: ContentfulCollectionGroupFragment[] = [];

  editorialCmsData.forEach((item) => {
    if (item.__typename === 'CollectionGroup') {
      currentCollectionGroup.push(item as ContentfulCollectionGroupFragment);
    } else {
      if (currentCollectionGroup.length > 0) {
        chunks.push({
          type: 'collection-group',
          items: [...currentCollectionGroup],
        });
        currentCollectionGroup = [];
      }

      switch (item.__typename) {
        case 'HeroProductListing': {
          const opts = getOptsForType<HeroProductListingOpts>(
            'HeroProductListing',
            sectionOpts,
          ).find((opt) => opt.opts?.type === 'watch editorial page')?.opts;

          chunks.push({
            type: 'hero-product-listing',
            item: item as ContentfulHeroProductListingFragment,
            opts,
          });
          break;
        }

        case 'Placeholder': {
          const opts = getOptsForType<PlaceholderProductListingOpts>(
            'Placeholder',
            sectionOpts,
          ).find((opt) => opt.opts?.type === 'product listing')?.opts;

          chunks.push({
            type: 'placeholder',
            item: item as ContentfulPlaceholderFragment,
            opts,
          });
          break;
        }

        default:
          chunks.push({
            type: 'contentful-section',
            item,
          });
      }
    }
  });

  if (currentCollectionGroup.length > 0) {
    chunks.push({
      type: 'collection-group',
      items: [...currentCollectionGroup],
    });
  }

  return chunks;
};

export async function resolveCollectionGroups(
  groups: ContentfulCollectionGroupFragment[],
  locale: string,
  draftMode: boolean,
): Promise<CombinedCollectionGroupItem[]> {
  const indexName = getIndexName('PRODUCT', locale);

  // Track original indices to preserve Contentful order
  const autoGroupsWithIndex: Array<{
    group: CollectionGroupWithAutomaticRefs;
    originalIndex: number;
  }> = [];
  const manualGroupsWithIndex: Array<{
    group: CollectionGroupWithManualRefs;
    originalIndex: number;
  }> = [];

  groups.forEach((g, index) => {
    if (g.productReferences?.__typename === 'AutomaticCollectionGroup') {
      autoGroupsWithIndex.push({
        group: g as CollectionGroupWithAutomaticRefs,
        originalIndex: index,
      });
    } else if (
      g.productReferences?.__typename ===
      'ManualCollectionGroupProductReferences'
    ) {
      manualGroupsWithIndex.push({
        group: g as CollectionGroupWithManualRefs,
        originalIndex: index,
      });
    }
  });

  // Fetch automatic groups
  const automaticQueries = autoGroupsWithIndex.map(({ group: groupItem }) => {
    const collection = splitEncoding(
      groupItem?.productReferences?.collection ?? '',
    ).cleaned;
    const algoliaRuleName = splitEncoding(
      groupItem?.productReferences?.algoliaRuleName ?? '',
    ).cleaned;

    const productType = collection === 'Eyewear' ? 'Eyewear' : 'Watch';

    const filters = applyProductConfigurationFilters(
      ProductConfigurationFilterType.PLP,
      productType,
      draftMode,
      ['Any', 'Eyewear']?.includes(collection || 'Any')
        ? ''
        : `collection:'${collection}'`,
    );

    const initialFacetFilters =
      groupItem?.productReferences?.algoliaFacetFilters ?? [];
    const facetFilters: Record<string, string[]> = initialFacetFilters
      .filter((f: string | null): f is string => f !== null)
      .reduce((acc: Record<string, string[]>, filter: string) => {
        const [key, value] = filter.split(':');
        if (!acc[key]) acc[key] = [];
        acc[key].push(value);
        return acc;
      }, {});

    return {
      indexName,
      query: algoliaRuleName,
      params: {
        filters,
        facetFilters: Object.entries(facetFilters).map(([key, values]) =>
          values.map((val: string) => `${key}:${val}`),
        ),
        hitsPerPage: groupItem?.productReferences?.maximumNumberOfProducts ?? 6,
      },
    };
  });
  const algoliaSsrSearchClient = createSearchClient({
    isPreview: draftMode,
    isSsr: true,
  });
  const { results: autoResults } =
    autoGroupsWithIndex.length > 0
      ? await algoliaSsrSearchClient.multipleQueries(automaticQueries)
      : { results: [] };

  // Fetch manual groups
  const manualObjectQueries = manualGroupsWithIndex.flatMap(({ group }) => {
    const skus = (group.productReferences?.manualProductReferences ?? [])
      .filter((sku: string | null): sku is string => sku !== null)
      .map((sku) => splitEncoding(sku).cleaned);
    return skus.map((sku: string) => ({ indexName, objectID: sku }));
  });
  const manualResults =
    manualObjectQueries.length > 0
      ? await algoliaSsrSearchClient.multipleGetObjects(manualObjectQueries)
      : [];
  const filteredManualResults = filterHits(manualResults, draftMode);

  // Build result array preserving original Contentful order
  const result: CombinedCollectionGroupItem[] = new Array(groups.length);

  // Place resolved automatic groups at their original positions
  autoGroupsWithIndex.forEach(({ group, originalIndex }, i) => {
    result[originalIndex] = {
      ...group,
      hits: (autoResults[i] as SearchResponse<WatchHit>)?.hits ?? [],
    };
  });

  // Place resolved manual groups at their original positions
  manualGroupsWithIndex.forEach(({ group, originalIndex }) => {
    const skus = (group.productReferences?.manualProductReferences ?? [])
      .filter((sku): sku is string => sku !== null)
      .map((sku) => splitEncoding(sku).cleaned);
    const matchingHits = filteredManualResults?.filter((hit: WatchHit) =>
      skus?.includes(hit.objectID),
    );
    result[originalIndex] = {
      ...group,
      hits: matchingHits,
    };
  });

  // Filter out any undefined entries (shouldn't happen, but safety check)
  return result.filter(Boolean);
}
